import React, { type ComponentPropsWithoutRef, useCallback } from 'react';

import { useIntl } from 'react-intl';

import Button, { type ButtonProps } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { ChatPillIcon } from '@atlaskit/rovo-agent-components/common/ui/ChatIcon';
import { AgentDropdownMenu as AgentDropdownMenuBase } from '@atlaskit/rovo-agent-components/ui/AgentDropdownMenu';
import { token } from '@atlaskit/tokens';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useAgentPermissions } from '../../../controllers/agent-permissions/index.ts';

import messages from './messages.ts';

const styles = cssMap({
	chatToAgentButtonContainer: {
		width: '100%',
	},
	chatPillButtonInline: { paddingInline: token('space.025') },
	chatPillText: {
		wordBreak: 'break-word',
		textAlign: 'left',
		whiteSpace: 'pre-wrap',
	},
	chatPillIconWrapper: {
		minWidth: '20px',
		height: '20px',
	},
	chatToAgentButtonWrapper: {
		display: 'flex',
		justifyContent: 'center',
		fontWeight: token('font.weight.medium'),
	},
});

type ChatToAgentButtonProps = {
	onClick: ButtonProps['onClick'];
};

export const ChatToAgentButton = ({ onClick }: ChatToAgentButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.chatToAgentButtonContainer}>
			<Button testId="view-agent-modal-chat-to-agent-button" shouldFitContainer onClick={onClick}>
				<Box xcss={styles.chatToAgentButtonWrapper}>
					<Inline space="space.050" xcss={styles.chatPillButtonInline}>
						<Box xcss={styles.chatPillIconWrapper}>
							<ChatPillIcon />
						</Box>
						<Box xcss={styles.chatPillText}>{formatMessage(messages.chatToAgentButton)}</Box>
					</Inline>
				</Box>
			</Button>
		</Box>
	);
};

type AgentDropdownMenuBaseProps = ComponentPropsWithoutRef<typeof AgentDropdownMenuBase>;
// Loading permissions and isAbleToGovernAgents are handled in the AgentDropdownMenu component
type AgentDropdownMenuProps = Omit<AgentDropdownMenuBaseProps, 'loadAgentPermissions'> &
	AssistanceServiceInputParam;

export const AgentDropdownMenu = (props: AgentDropdownMenuProps): React.JSX.Element => {
	const { assistanceServiceParams, ...baseProps } = props;

	const { refetch } = useAgentPermissions({
		agentId: props.agentId,
		enabled: false,
		assistanceServiceParams,
	});

	const loadAgentPermissions = useCallback(async () => {
		const response = await refetch();

		return {
			isCreateEnabled: response?.permissions.AGENT_CREATE?.permitted ?? false,
			isEditEnabled: response?.permissions.AGENT_UPDATE?.permitted ?? false,
			isDeleteEnabled: response?.permissions.AGENT_DEACTIVATE?.permitted ?? false,
		};
	}, [refetch]);

	return (
		<AgentDropdownMenuBase
			// We need to typecast back to the base type because using `Omit` doesn't preserve discriminated unions, causing type errors
			{...(baseProps as AgentDropdownMenuBaseProps)}
			loadAgentPermissions={loadAgentPermissions}
		/>
	);
};
